
import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { FeaturePermission } from '@/types/userRole';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { ChevronRight } from 'lucide-react';

interface PermissionsListProps {
  category: string;
  permissions: FeaturePermission[];
  selectedPermissions: string[];
  onTogglePermission: (permissionId: string) => void;
  onToggleAll: (category: string, enabled: boolean) => void;
}

const PermissionsList: React.FC<PermissionsListProps> = ({
  category,
  permissions,
  selectedPermissions,
  onTogglePermission,
  onToggleAll
}) => {
  const isAllSelected = permissions.every(p => selectedPermissions.includes(p.id));
  const isSomeSelected = permissions.some(p => selectedPermissions.includes(p.id));

  const handleCategoryToggle = () => {
    onToggleAll(category, !isAllSelected);
  };

  // Format category name for display
  const formatCategoryName = (cat: string) => {
    return cat.charAt(0).toUpperCase() + cat.slice(1);
  };

  return (
    <Collapsible defaultOpen={true} className="border rounded-md overflow-hidden">
      <div className="bg-muted/30 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`category-${category}`}
            checked={isAllSelected}
            className={isSomeSelected && !isAllSelected ? "opacity-50" : ""}
            onCheckedChange={handleCategoryToggle}
            ref={(input) => {
              if (input) {
                (input as unknown as HTMLInputElement).indeterminate = isSomeSelected && !isAllSelected;
              }
            }}
          />
          <label
            htmlFor={`category-${category}`}
            className="text-sm font-medium cursor-pointer"
          >
            {formatCategoryName(category)}
          </label>
        </div>
        <CollapsibleTrigger className="hover:bg-muted/50 p-1 rounded">
          <ChevronRight className="h-4 w-4 ui-open:rotate-90 ui-open:transform transition-all" />
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="p-2 space-y-1">
          {permissions.map(permission => (
            <div key={permission.id} className="flex items-start gap-2 py-1 px-2 hover:bg-muted/20 rounded">
              <Checkbox
                id={permission.id}
                checked={selectedPermissions.includes(permission.id)}
                onCheckedChange={() => onTogglePermission(permission.id)}
                className="mt-1"
              />
              <div className="space-y-1">
                <label
                  htmlFor={permission.id}
                  className="text-sm font-medium cursor-pointer"
                >
                  {permission.name}
                </label>
                <p className="text-xs text-muted-foreground">{permission.description}</p>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default PermissionsList;
